import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  AlertTriangle, 
  XCircle, 
  CheckCircle, 
  CreditCard, 
  User, 
  Shield, 
  Phone, 
  Mail, 
  MapPin,
  Save,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  FileText,
  History,
  Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeactivatePage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, logout, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Password state
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Security settings
  const [security, setSecurity] = useState({
    two_factor_enabled: false,
    verification_code: '',
  });
  const [showVerificationInput, setShowVerificationInput] = useState(false);

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    show_email_publicly: false,
    show_phone_publicly: false,
    allow_marketing_emails: true,
    allow_analytics: true,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSubscription();
    fetchProfile();
    fetchPaymentHistory();
  }, [isAuthenticated, token]);

  const fetchProfile = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        withCredentials: true
      });
      const userData = response.data;
      setProfile({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        city: userData.city || '',
        state: userData.state || '',
        zip_code: userData.zip_code || '',
      });
      setSecurity({
        two_factor_enabled: userData.two_factor_enabled || false,
        verification_code: '',
      });
      setPrivacy({
        show_email_publicly: userData.show_email_publicly || false,
        show_phone_publicly: userData.show_phone_publicly || false,
        allow_marketing_emails: userData.allow_marketing_emails !== false,
        allow_analytics: userData.allow_analytics !== false,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch profile:', error);
      }
    }
  };

  const fetchSubscription = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/my-subscription`, {
        headers,
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch subscription:', error);
      }
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/payments/history`, {
        headers,
        withCredentials: true
      });
      setPaymentHistory(response.data.transactions || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch payment history:', error);
      }
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/auth/profile`, profile, {
        headers,
        withCredentials: true
      });
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSavingPassword(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, {
        headers,
        withCredentials: true
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSavePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/auth/privacy`, privacy, {
        headers,
        withCredentials: true
      });
      toast.success('Privacy settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update privacy settings');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleToggle2FA = async () => {
    if (!security.two_factor_enabled) {
      setShowVerificationInput(true);
    } else {
      setSavingSecurity(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API}/auth/2fa/disable`, {}, {
          headers,
          withCredentials: true
        });
        setSecurity(prev => ({ ...prev, two_factor_enabled: false }));
        toast.success('Two-factor authentication disabled');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
      } finally {
        setSavingSecurity(false);
      }
    }
  };

  const handleEnable2FA = async () => {
    if (!security.verification_code || security.verification_code.length < 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }
    
    setSavingSecurity(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/2fa/enable`, {
        verification_code: security.verification_code
      }, {
        headers,
        withCredentials: true
      });
      setSecurity(prev => ({ ...prev, two_factor_enabled: true, verification_code: '' }));
      setShowVerificationInput(false);
      toast.success('Two-factor authentication enabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable 2FA');
    } finally {
      setSavingSecurity(false);
    }
  };

  const handleCancelSubscription = async (planType) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/subscription/cancel`, 
        { plan_type: planType },
        { headers, withCredentials: true }
      );
      toast.success(`Your ${planType} subscription has been cancelled`);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (confirmText !== 'DEACTIVATE') {
      toast.error('Please type DEACTIVATE to confirm');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/account/deactivate`, {}, {
        headers,
        withCredentials: true
      });
      toast.success('Your account has been deactivated');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate account');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/download-data`, {
        headers,
        withCredentials: true
      });
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data.json';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded');
    } catch (error) {
      toast.error('Failed to download data');
    }
  };

  const hasActiveSubscription = subscription?.browser_subscription === 'browser_pro' || 
    ['owner_basic', 'owner_pro', 'owner_premium'].includes(subscription?.owner_subscription);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="settings-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-deep mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="font-display text-3xl font-bold text-stone-900">
              Account Settings
            </h1>
            <p className="text-stone-600 mt-1">
              Manage your profile, security, privacy, and subscriptions
            </p>
          </div>

          {/* Profile Information */}
          <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-deep" />
              Profile Information
            </h2>

            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-stone-700">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={profile.name}
                  onChange={handleProfileChange}
                  placeholder="Your full name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-stone-700">Email Address</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-stone-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={profile.email}
                    onChange={handleProfileChange}
                    placeholder="your@email.com"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4 text-stone-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={profile.phone}
                    onChange={handleProfileChange}
                    placeholder="+1 (555) 123-4567"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-stone-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-stone-400" />
                  Physical Address
                </Label>
                <div className="space-y-3 mt-2">
                  <Input
                    name="address"
                    value={profile.address}
                    onChange={handleProfileChange}
                    placeholder="Street address"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      name="city"
                      value={profile.city}
                      onChange={handleProfileChange}
                      placeholder="City"
                    />
                    <Input
                      name="state"
                      value={profile.state}
                      onChange={handleProfileChange}
                      placeholder="State"
                    />
                    <Input
                      name="zip_code"
                      value={profile.zip_code}
                      onChange={handleProfileChange}
                      placeholder="ZIP Code"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="w-full bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Password Section */}
          <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-deep" />
              Change Password
            </h2>

            <div className="space-y-5">
              <div>
                <Label htmlFor="current_password" className="text-stone-700">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwords.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-stone-700">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwords.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password (min 8 characters)"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-stone-700">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={passwords.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  className="mt-1"
                />
              </div>

              <Button
                onClick={handleChangePassword}
                disabled={savingPassword || !passwords.current_password || !passwords.new_password}
                className="w-full bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
              >
                {savingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-deep" />
              Security Settings
            </h2>

            <div className="space-y-5">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-stone-900">Two-Step Verification</h3>
                  <p className="text-sm text-stone-500">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <Switch
                  checked={security.two_factor_enabled}
                  onCheckedChange={handleToggle2FA}
                  disabled={savingSecurity}
                />
              </div>

              {showVerificationInput && !security.two_factor_enabled && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-stone-700 mb-3">
                    Enter a 6-digit code that will be used for verification:
                  </p>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      maxLength={6}
                      value={security.verification_code}
                      onChange={(e) => setSecurity(prev => ({ 
                        ...prev, 
                        verification_code: e.target.value.replace(/\D/g, '').slice(0, 6)
                      }))}
                      placeholder="123456"
                      className="flex-1 text-center text-lg tracking-widest"
                    />
                    <Button
                      onClick={handleEnable2FA}
                      disabled={savingSecurity || security.verification_code.length < 6}
                      className="bg-emerald-deep hover:bg-emerald-deep/90"
                    >
                      {savingSecurity ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowVerificationInput(false);
                        setSecurity(prev => ({ ...prev, verification_code: '' }));
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {security.two_factor_enabled && (
                <div className="flex items-center gap-2 text-sm text-emerald-deep">
                  <CheckCircle className="w-4 h-4" />
                  Two-step verification is enabled
                </div>
              )}
            </div>
          </div>

          {/* Privacy & Data Settings */}
          <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-deep" />
              Privacy & Data Settings
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-stone-900">Show Email Publicly</h3>
                  <p className="text-sm text-stone-500">Allow others to see your email on your business listings</p>
                </div>
                <Switch
                  checked={privacy.show_email_publicly}
                  onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, show_email_publicly: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-stone-900">Show Phone Publicly</h3>
                  <p className="text-sm text-stone-500">Allow others to see your phone number</p>
                </div>
                <Switch
                  checked={privacy.show_phone_publicly}
                  onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, show_phone_publicly: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-stone-900">Marketing Emails</h3>
                  <p className="text-sm text-stone-500">Receive promotional emails and newsletters</p>
                </div>
                <Switch
                  checked={privacy.allow_marketing_emails}
                  onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, allow_marketing_emails: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-stone-900">Analytics & Improvements</h3>
                  <p className="text-sm text-stone-500">Help us improve by sharing usage data</p>
                </div>
                <Switch
                  checked={privacy.allow_analytics}
                  onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, allow_analytics: checked }))}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSavePrivacy}
                  disabled={savingPrivacy}
                  className="flex-1 bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
                >
                  {savingPrivacy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Privacy Settings
                </Button>
                <Button
                  onClick={handleDownloadData}
                  variant="outline"
                  className="rounded-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download My Data
                </Button>
              </div>
            </div>
          </div>

          {/* Membership & Payment History */}
          <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-deep" />
              Membership & Payment History
            </h2>

            {/* Current Membership */}
            <div className="mb-6">
              <h3 className="font-semibold text-stone-900 mb-3">Current Membership</h3>
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                {hasActiveSubscription ? (
                  <div className="space-y-2">
                    {subscription?.browser_subscription === 'browser_pro' && (
                      <div className="flex justify-between items-center">
                        <span className="text-stone-700">Browser Pro</span>
                        <span className="font-semibold text-emerald-deep">$9.99/month</span>
                      </div>
                    )}
                    {subscription?.owner_subscription && (
                      <div className="flex justify-between items-center">
                        <span className="text-stone-700">
                          {subscription.owner_subscription === 'owner_basic' && 'Basic Listing'}
                          {subscription.owner_subscription === 'owner_pro' && 'Pro Listing'}
                          {subscription.owner_subscription === 'owner_premium' && 'Premium Listing'}
                        </span>
                        <span className="font-semibold text-emerald-deep">
                          {subscription.owner_subscription === 'owner_basic' && '$19.99/month'}
                          {subscription.owner_subscription === 'owner_pro' && '$49.99/month'}
                          {subscription.owner_subscription === 'owner_premium' && '$99.99/month'}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-stone-600">No active subscriptions</p>
                )}
              </div>
            </div>

            {/* Payment History */}
            <div>
              <h3 className="font-semibold text-stone-900 mb-3">Payment History</h3>
              {paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map((payment) => (
                    <div key={payment.transaction_id || payment.stripe_session_id || `payment-${payment.created_at}`} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium text-stone-900">{payment.description || payment.plan_id || 'Payment'}</p>
                        <p className="text-sm text-stone-500">{formatDate(payment.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-stone-900">${payment.amount?.toFixed(2)}</p>
                        <p className={`text-sm ${payment.status === 'completed' ? 'text-emerald-deep' : 'text-amber-600'}`}>
                          {payment.status === 'completed' ? 'Paid' : payment.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-stone-50 rounded-lg text-center text-stone-500">
                  No payment history found
                </div>
              )}
            </div>
          </div>

          {/* Active Subscriptions */}
          {hasActiveSubscription && (
            <div className="bg-white rounded-xl p-6 border border-stone-200 mb-6">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-deep" />
                Manage Subscriptions
              </h2>

              <div className="space-y-4">
                {subscription?.browser_subscription === 'browser_pro' && (
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-stone-900">Browser Pro</h3>
                      <p className="text-sm text-stone-500">$9.99/month - Unlimited browsing</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel Browser Pro?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll lose access to unlimited browsing and advanced filters.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleCancelSubscription('browser')}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Yes, Cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}

                {['owner_basic', 'owner_pro', 'owner_premium'].includes(subscription?.owner_subscription) && (
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-stone-900">
                        {subscription.owner_subscription === 'owner_basic' && 'Basic Listing'}
                        {subscription.owner_subscription === 'owner_pro' && 'Pro Listing'}
                        {subscription.owner_subscription === 'owner_premium' && 'Premium Listing'}
                      </h3>
                      <p className="text-sm text-stone-500">
                        {subscription.owner_subscription === 'owner_basic' && '$19.99/month - 1 listing'}
                        {subscription.owner_subscription === 'owner_pro' && '$49.99/month - Up to 5 listings'}
                        {subscription.owner_subscription === 'owner_premium' && '$99.99/month - Unlimited listings'}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel Business Listing Subscription?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Your business listings will be hidden from the directory.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleCancelSubscription('owner')}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Yes, Cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Danger Zone */}
          <div className="bg-white rounded-xl p-6 border border-red-200">
            <h2 className="font-display text-xl font-semibold text-red-600 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </h2>

            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <h3 className="font-semibold text-stone-900 mb-2">Deactivate Account</h3>
              <p className="text-sm text-stone-600 mb-4">
                Permanently deactivate your account and remove all your data. This action cannot be undone.
              </p>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
                    <XCircle className="w-4 h-4 mr-2" />
                    Deactivate My Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-red-600">
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-4">
                      <p>This will permanently delete all your data.</p>
                      <div className="pt-4">
                        <Label htmlFor="confirm" className="text-stone-700">
                          Type <span className="font-bold">DEACTIVATE</span> to confirm:
                        </Label>
                        <Input
                          id="confirm"
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="DEACTIVATE"
                          className="mt-2"
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setConfirmText('')}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeactivateAccount}
                      disabled={confirmText !== 'DEACTIVATE' || loading}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {loading ? 'Deactivating...' : 'Permanently Deactivate'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DeactivatePage;
